// Минималистичный SVG Sankey-флоу: 3 уровня (источники доходов → пул → назначения).
// Без зависимостей. Не идеальный академический sankey, а простой three-column
// flow chart с пропорциональными лентами — этого достаточно для wow-эффекта.

import { useMemo } from 'react';
import type { SankeyResponse } from '../../api/types';
import { formatWithCurrency } from '../../lib/money';

type Props = {
  data: SankeyResponse;
  currency: string;
  height?: number;
};

const NODE_W = 80;
const NODE_GAP = 6;
const COL_GAP_PX = 0; // не используется напрямую — sized через viewBox
const PAD_X = 6;

type LayoutNode = {
  id: string;
  label: string;
  x: number;
  y: number;
  h: number;
  type: 'income' | 'pool' | 'category' | 'sink';
  totalMinor: bigint;
};

export function Sankey({ data, currency, height = 320 }: Props): JSX.Element {
  const layout = useMemo(() => buildLayout(data, height), [data, height]);

  if (data.nodes.length === 0 || data.links.length === 0 || layout.totalMinor === 0n) {
    return <div className="empty-mini">Нет данных за период для диаграммы потока</div>;
  }

  return (
    <div className="sankey-wrap">
      <svg width="100%" viewBox={`0 0 ${layout.viewW} ${height}`} preserveAspectRatio="xMidYMid meet">
        {/* Связи */}
        {layout.links.map((l, i) => (
          <path key={i} d={l.path} style={{ fill: l.color }} opacity={0.4} />
        ))}
        {/* Узлы */}
        {layout.nodes.map((n) => (
          <g key={n.id}>
            <rect
              x={n.x}
              y={n.y}
              width={NODE_W}
              height={n.h}
              rx={2}
              style={{ fill: colorForType(n.type) }}
            />
            <text
              x={n.x + NODE_W / 2}
              y={n.y + n.h / 2 + 4}
              fontSize={11}
              textAnchor="middle"
              fontWeight="600"
              style={{ pointerEvents: 'none', fill: 'var(--paper)' }}
            >
              {truncate(n.label, 11)}
            </text>
          </g>
        ))}
      </svg>
      <div className="sankey-legend">
        <span className="legend-dot" style={{ background: colorForType('income') }} />
        Доходы
        <span className="legend-dot" style={{ background: colorForType('category') }} />
        Расходы
        <span className="legend-dot" style={{ background: colorForType('sink') }} />
        Долги и накопления
      </div>
      <div className="sankey-totals">
        <div>
          <span className="muted">Доход:</span>{' '}
          <b className="money" style={{ color: 'var(--positive)' }}>
            {formatWithCurrency(data.total_income_minor, currency)}
          </b>
        </div>
        <div>
          <span className="muted">Расходы:</span>{' '}
          <b className="money">{formatWithCurrency(data.total_expense_minor, currency)}</b>
        </div>
        <div>
          <span className="muted">На долги:</span>{' '}
          <b className="money">{formatWithCurrency(data.debt_paid_minor, currency)}</b>
        </div>
        <div>
          <span className="muted">Накопления:</span>{' '}
          <b className="money" style={{ color: 'var(--positive)' }}>
            {formatWithCurrency(data.savings_minor, currency)}
          </b>
        </div>
      </div>
    </div>
  );
}

// Роли узлов — токены, а не хексы: диаграмма обязана читаться в обеих темах.
function colorForType(t: LayoutNode['type']): string {
  switch (t) {
    case 'income':
      return 'var(--positive)';
    case 'pool':
      return 'var(--ink)';
    case 'category':
      return 'var(--ink-2)';
    case 'sink':
      return 'var(--accent)';
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function buildLayout(data: SankeyResponse, height: number): {
  nodes: LayoutNode[];
  links: Array<{ path: string; color: string }>;
  viewW: number;
  totalMinor: bigint;
} {
  // Группируем links по source/target.
  const incomingTo = new Map<string, bigint>();
  const outgoingFrom = new Map<string, bigint>();
  for (const l of data.links) {
    const v = BigInt(l.value_minor);
    incomingTo.set(l.target, (incomingTo.get(l.target) ?? 0n) + v);
    outgoingFrom.set(l.source, (outgoingFrom.get(l.source) ?? 0n) + v);
  }

  // Тотал — берём по pool.
  const poolTotal = incomingTo.get('pool') ?? 0n;

  if (poolTotal === 0n) {
    return { nodes: [], links: [], viewW: 800, totalMinor: 0n };
  }

  const ints = data.nodes.filter((n) => n.type === 'income');
  const sinksAndCats = data.nodes.filter((n) => n.type === 'category' || n.type === 'sink');
  const poolNode = data.nodes.find((n) => n.type === 'pool');

  const cols = [
    { nodes: ints, x: PAD_X },
    { nodes: poolNode ? [poolNode] : [], x: PAD_X + NODE_W + 120 },
    { nodes: sinksAndCats, x: PAD_X + NODE_W * 2 + 240 },
  ];
  const viewW = PAD_X * 2 + NODE_W * 3 + 240;

  // Высоты — пропорция к poolTotal. Минимум 14.
  const layoutNodes: LayoutNode[] = [];
  for (const col of cols) {
    let yCursor = 10;
    const totalHForCol = col.nodes.reduce((acc, n) => {
      const v =
        n.type === 'income'
          ? outgoingFrom.get(n.id) ?? 0n
          : n.type === 'pool'
            ? poolTotal
            : incomingTo.get(n.id) ?? 0n;
      const h = Number((v * BigInt(height - 40) * 1000n) / poolTotal) / 1000;
      return acc + Math.max(14, h);
    }, 0);
    const scale = totalHForCol > height - 40 ? (height - 40) / totalHForCol : 1;
    for (const n of col.nodes) {
      const v =
        n.type === 'income'
          ? outgoingFrom.get(n.id) ?? 0n
          : n.type === 'pool'
            ? poolTotal
            : incomingTo.get(n.id) ?? 0n;
      const rawH = Number((v * BigInt(height - 40) * 1000n) / poolTotal) / 1000;
      const h = Math.max(14, rawH) * scale;
      layoutNodes.push({
        id: n.id,
        label: n.label,
        x: col.x,
        y: yCursor,
        h,
        type: n.type,
        totalMinor: v,
      });
      yCursor += h + NODE_GAP;
    }
  }

  // Кривые-ленты между нодами.
  const nodeMap = new Map(layoutNodes.map((n) => [n.id, n]));
  // Для каждого узла отслеживаем «текущий y-курсор» исходящих/входящих лент.
  const outCursor = new Map<string, number>();
  const inCursor = new Map<string, number>();
  for (const n of layoutNodes) {
    outCursor.set(n.id, n.y);
    inCursor.set(n.id, n.y);
  }

  const links: Array<{ path: string; color: string }> = [];
  for (const l of data.links) {
    const src = nodeMap.get(l.source);
    const tgt = nodeMap.get(l.target);
    if (!src || !tgt) continue;
    const value = BigInt(l.value_minor);
    const ribbonH = Math.max(2, (Number(value) / Number(poolTotal)) * (height - 40));

    const srcY = outCursor.get(src.id) ?? src.y;
    const tgtY = inCursor.get(tgt.id) ?? tgt.y;
    const x0 = src.x + NODE_W;
    const x1 = tgt.x;
    const cpX = (x0 + x1) / 2;

    // Кубическая Безье-лента (как в d3-sankey).
    const pathTop = `M ${x0} ${srcY} C ${cpX} ${srcY}, ${cpX} ${tgtY}, ${x1} ${tgtY}`;
    const pathBottom = `L ${x1} ${tgtY + ribbonH} C ${cpX} ${tgtY + ribbonH}, ${cpX} ${srcY + ribbonH}, ${x0} ${srcY + ribbonH} Z`;
    const path = `${pathTop} ${pathBottom}`;

    const color =
      tgt.type === 'category'
        ? 'var(--ink-3)'
        : tgt.type === 'sink'
          ? tgt.id === 'savings'
            ? 'var(--positive)'
            : 'var(--accent)'
          : 'var(--positive)';

    links.push({ path, color });
    outCursor.set(src.id, srcY + ribbonH);
    inCursor.set(tgt.id, tgtY + ribbonH);
  }

  return { nodes: layoutNodes, links, viewW, totalMinor: poolTotal };
}
